import InvestigationService from '@/services/investigation';
import { toStructuredFormulation } from '@/modules/formulary/utils/formulationString';
import { buildSummary, formatFlowRate } from '@/modules/formulary/utils/formulationSummary';
import { trendWindow } from '@/modules/formulary/utils/trendWindow';

// Owns the server data behind the Investigate page. Local UI state (which tab is
// open, whether the search bar is expanded) stays in the view.
//
// Formulations and usage trends are scoped by date range, facility, population
// and the min-infusions floor. ASHP standards and the RTU catalog are properties
// of the DRUG, so they depend only on the medication name and are refetched only
// when it changes — hence setInvestigationScopedData alongside the full load.
//
// Facilities and populations are cross-page lookups owned by the `reference`
// module, not here.
//
// Each async action takes a monotonic token and discards a response whose token
// is stale: without it, changing a filter twice quickly lets a slow first
// response land after and overwrite a fast second one, leaving the table on one
// scope and the chart on another.
//
// Row ORDER and the table's summary AGGREGATES both belong to the endpoint, not
// here: they are properties of the filtered row set the server already scans,
// and re-deriving them client-side is what would let the table disagree with
// itself the moment that row list is paged or truncated. This module reshapes
// field names and coerces types — it does not filter, sort or aggregate.
// See INVESTIGATE_DATA_CONTRACTS.md #2.

let formulationToken = 0;
let trendsToken = 0;
let rtuToken = 0;
let ashpToken = 0;

// Splits the formulation string via the shared parser so the display columns
// don't each re-implement the regex. The raw string halves are kept because the
// unit-preference formatter falls back to them when the parse fails.
function shapeFormulationRow(row) {
  const formulation = row.formulation || '';
  const [concentrationPart = formulation, volumePart = ''] = formulation.split(' in ');
  const structured = toStructuredFormulation(formulation);

  return {
    formulation,
    concentration: concentrationPart,
    volume: volumePart,
    // Prefer the endpoint's structured columns; fall back to the parse.
    concentrationValue: row.concentrationValue ?? structured?.concentration ?? null,
    concentrationUnit: row.concentrationUnit ?? structured?.unit ?? null,
    volumeValue: row.volumeValue ?? structured?.volume ?? null,
    volumeUnit: row.volumeUnit ?? 'mL',
    infusions: row.infusions ?? 0,
    // The endpoint's only waste field, so Investigate and Analyze can't disagree.
    waste: row.wasteWeightedPct ?? row.waste_weighted_pct ?? null,
    min_rate: row.minRate ?? row.min_rate ?? null,
    max_rate: row.maxRate ?? row.max_rate ?? null,
  };
}

// The endpoint's summary block -> the shape the table renders.
//
// Formatting only: the arithmetic happened server-side. `averageWaste` is a
// string because SummaryMetricCell interpolates it directly, and null becomes
// 'N/A' rather than '0.0' so "no waste figure at all" doesn't read as "no
// waste".
function shapeSummary(summary) {
  // null/undefined/'' first: Number() turns all three into 0, so a bare
  // Number.isFinite check would render an absent figure as '0.0' instead of
  // 'N/A'.
  const numeric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const averageWaste = numeric(summary.averageWastePct);
  const minFlowRate = numeric(summary.minFlowRate);
  const maxFlowRate = numeric(summary.maxFlowRate);

  return {
    totalInfusions: numeric(summary.totalInfusions) || 0,
    averageWaste: averageWaste === null ? 'N/A' : averageWaste.toFixed(1),
    minFlowRate: minFlowRate === null ? 'N/A' : formatFlowRate(minFlowRate),
    maxFlowRate: maxFlowRate === null ? 'N/A' : formatFlowRate(maxFlowRate),
  };
}

// `year`/`month` may arrive as numbers or strings depending on the driver.
function shapeTrendRow(row) {
  return {
    year: Number(row.year),
    month: Number(row.month),
    formulation: row.formulation || '',
    infusions: Number(row.infusions) || 0,
  };
}

export default {
  state: {
    // Set by the view from the shared filter state before dispatching a load.
    scope: {
      medicationName: '',
      startDate: '',
      endDate: '',
      facilities: [],
      patientPopulations: [],
      minInfusions: 5,
    },

    formulationData: [],
    // The table's footer row, aggregated server-side over the whole filtered
    // set. Null until the first response lands.
    formulationSummary: null,
    formulationDataLoaded: false,

    usageTrends: [],
    usageTrendsLoaded: false,

    rtuFormulations: [],
    rtuFormulationsLoaded: false,

    ashpConcentration: null,
    ashpConcentrationLoaded: false,
  },

  mutations: {
    SET_INVESTIGATION_SCOPE(state, scope) {
      state.scope = { ...state.scope, ...scope };
    },
    SET_INVESTIGATION_FORMULATION_DATA(state, rows) {
      state.formulationData = rows;
    },
    SET_INVESTIGATION_FORMULATION_SUMMARY(state, summary) {
      state.formulationSummary = summary;
    },
    SET_INVESTIGATION_FORMULATION_DATA_LOADED(state, loaded) {
      state.formulationDataLoaded = loaded;
    },
    SET_INVESTIGATION_USAGE_TRENDS(state, rows) {
      state.usageTrends = rows;
    },
    SET_INVESTIGATION_USAGE_TRENDS_LOADED(state, loaded) {
      state.usageTrendsLoaded = loaded;
    },
    SET_INVESTIGATION_RTU_FORMULATIONS(state, rtuFormulations) {
      state.rtuFormulations = rtuFormulations;
    },
    SET_INVESTIGATION_RTU_FORMULATIONS_LOADED(state, loaded) {
      state.rtuFormulationsLoaded = loaded;
    },
    SET_INVESTIGATION_ASHP_CONCENTRATION(state, ashpConcentration) {
      state.ashpConcentration = ashpConcentration;
    },
    SET_INVESTIGATION_ASHP_CONCENTRATION_LOADED(state, loaded) {
      state.ashpConcentrationLoaded = loaded;
    },
    RESET_INVESTIGATION(state) {
      state.formulationData = [];
      state.formulationSummary = null;
      state.formulationDataLoaded = false;
      state.usageTrends = [];
      state.usageTrendsLoaded = false;
      state.rtuFormulations = [];
      state.rtuFormulationsLoaded = false;
      state.ashpConcentration = null;
      state.ashpConcentrationLoaded = false;
    },
  },

  actions: {
    setInvestigationScope({ commit }, scope) {
      commit('SET_INVESTIGATION_SCOPE', scope);
    },

    resetInvestigation({ commit }) {
      // So a late response can't repopulate a medication already navigated away from.
      formulationToken += 1;
      trendsToken += 1;
      rtuToken += 1;
      ashpToken += 1;
      commit('RESET_INVESTIGATION');
    },

    setInvestigationFormulationData({ commit, state }) {
      const token = ++formulationToken;
      commit('SET_INVESTIGATION_FORMULATION_DATA_LOADED', false);

      return InvestigationService.getFormulations(state.scope)
        .then((response) => {
          if (formulationToken !== token) return null;
          const rows = response.formulations || response.results || [];
          // Committed in the order received. The endpoint sorts by infusions
          // descending and has already dropped the rows with none in range, so
          // re-sorting here would only be a second opinion about the same
          // question — and one that reads the rounded row values.
          const shaped = rows.map(shapeFormulationRow);

          commit('SET_INVESTIGATION_FORMULATION_DATA', shaped);
          // Falls back to computing it over the committed rows when the
          // response carries no summary block, so an endpoint that ships the
          // rows before the aggregates still renders a footer. Remove the
          // fallback once /formulations returns `summary` for certain.
          commit(
            'SET_INVESTIGATION_FORMULATION_SUMMARY',
            response.summary ? shapeSummary(response.summary) : buildSummary(shaped)
          );
          return shaped;
        })
        .catch((error) => {
          if (formulationToken !== token) return null;
          // eslint-disable-next-line no-console
          console.error('Failed to load formulation data:', error);
          commit('SET_INVESTIGATION_FORMULATION_DATA', []);
          commit('SET_INVESTIGATION_FORMULATION_SUMMARY', null);
          return [];
        })
        .finally(() => {
          if (formulationToken === token) {
            commit('SET_INVESTIGATION_FORMULATION_DATA_LOADED', true);
          }
        });
    },

    // The window is derived here rather than in the view, so the chart can't
    // drift from the table's date range.
    setInvestigationUsageTrends({ commit, state }) {
      const token = ++trendsToken;
      const window = trendWindow(state.scope.endDate);

      if (!window) {
        commit('SET_INVESTIGATION_USAGE_TRENDS', []);
        commit('SET_INVESTIGATION_USAGE_TRENDS_LOADED', true);
        return Promise.resolve([]);
      }

      commit('SET_INVESTIGATION_USAGE_TRENDS_LOADED', false);

      return InvestigationService.getUsageTrends({
        ...state.scope,
        startDate: window.start,
        endDate: window.end,
      })
        .then((response) => {
          if (trendsToken !== token) return null;
          const rows = (response.trends || response.results || []).map(shapeTrendRow);
          commit('SET_INVESTIGATION_USAGE_TRENDS', rows);
          return rows;
        })
        .catch((error) => {
          if (trendsToken !== token) return null;
          // eslint-disable-next-line no-console
          console.error('Failed to load usage trends:', error);
          commit('SET_INVESTIGATION_USAGE_TRENDS', []);
          return [];
        })
        .finally(() => {
          if (trendsToken === token) {
            commit('SET_INVESTIGATION_USAGE_TRENDS_LOADED', true);
          }
        });
    },

    // An empty list is ordinary ("no RTU products for this drug"), so a failure
    // resolves to empty rather than surfacing an error.
    setInvestigationRtuFormulations({ commit, state }) {
      const token = ++rtuToken;
      commit('SET_INVESTIGATION_RTU_FORMULATIONS_LOADED', false);

      return InvestigationService.getRtuFormulations(state.scope.medicationName)
        .then((response) => {
          if (rtuToken !== token) return null;
          const rtuFormulations = response.rtuFormulations || [];
          commit('SET_INVESTIGATION_RTU_FORMULATIONS', rtuFormulations);
          return rtuFormulations;
        })
        .catch(() => {
          if (rtuToken !== token) return null;
          commit('SET_INVESTIGATION_RTU_FORMULATIONS', []);
          return [];
        })
        .finally(() => {
          if (rtuToken === token) {
            commit('SET_INVESTIGATION_RTU_FORMULATIONS_LOADED', true);
          }
        });
    },

    // Absent for drugs ASHP hasn't standardized; null renders as N/A, not as
    // misaligned.
    setInvestigationAshpConcentration({ commit, state }) {
      const token = ++ashpToken;
      commit('SET_INVESTIGATION_ASHP_CONCENTRATION_LOADED', false);

      return InvestigationService.getAshpConcentration(state.scope.medicationName)
        .then((response) => {
          if (ashpToken !== token) return null;
          commit('SET_INVESTIGATION_ASHP_CONCENTRATION', response);
          return response;
        })
        .catch(() => {
          if (ashpToken !== token) return null;
          commit('SET_INVESTIGATION_ASHP_CONCENTRATION', null);
          return null;
        })
        .finally(() => {
          if (ashpToken === token) {
            commit('SET_INVESTIGATION_ASHP_CONCENTRATION_LOADED', true);
          }
        });
    },

    // Rejections are absorbed per-action, so this resolves once all settle.
    setInvestigationData({ dispatch }) {
      return Promise.all([
        dispatch('setInvestigationFormulationData'),
        dispatch('setInvestigationUsageTrends'),
        dispatch('setInvestigationRtuFormulations'),
        dispatch('setInvestigationAshpConcentration'),
      ]);
    },

    // A filter change: ASHP and RTU are per-medication, so don't refetch them.
    setInvestigationScopedData({ dispatch }) {
      return Promise.all([dispatch('setInvestigationFormulationData'), dispatch('setInvestigationUsageTrends')]);
    },
  },

  getters: {
    investigationScope: (state) => state.scope,
    investigationFormulationData: (state) => state.formulationData,
    // buildSummary over an empty list, so the footer renders zeroes rather than
    // blanks before the first response.
    investigationFormulationSummary: (state) => state.formulationSummary || buildSummary([]),
    investigationFormulationDataLoaded: (state) => state.formulationDataLoaded,
    investigationUsageTrends: (state) => state.usageTrends,
    investigationUsageTrendsLoaded: (state) => state.usageTrendsLoaded,
    investigationRtuFormulations: (state) => state.rtuFormulations,
    investigationRtuFormulationsLoaded: (state) => state.rtuFormulationsLoaded,
    investigationAshpConcentration: (state) => state.ashpConcentration,
    investigationAshpConcentrationLoaded: (state) => state.ashpConcentrationLoaded,
  },
};
